/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import adminServices from '../services/adminServices';

export default function AnnouncementBell() {
  const { userInfo } = useSelector((state) => state);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  const fetchAnnouncements = async () => {
    const res = await adminServices.getAnnouncements(5);
    if (res.ok) {
      const list = res.data || [];
      setAnnouncements(list);
      setUnreadCount(list.filter(a => !a.isRead).length);
    }
  };

  useEffect(() => {
    if (userInfo?.role !== 'AUTH_USER' && userInfo?.role !== 'MANAGER') return;
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 300000); // 5 minutes
    const handleVisibility = () => { if (!document.hidden) fetchAnnouncements(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [userInfo?.id]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const announcementPath = userInfo?.role === 'MANAGER' ? '/app/announcement' : '/app/announcements';

  const handleClick = async (a) => {
    if (!a.isRead) {
      await adminServices.markAnnouncementRead(a.id);
      setAnnouncements(prev => prev.map(x => x.id === a.id ? { ...x, isRead: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    navigate(announcementPath, { state: { announcementId: a.id } });
  };

  const handleViewAll = () => { setOpen(false); navigate(announcementPath); };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
  };

  if (userInfo?.role !== 'AUTH_USER' && userInfo?.role !== 'MANAGER') return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-full hover:bg-gray-100 transition">
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Announcements</p>
            {unreadCount > 0 && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{unreadCount} new</span>}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {announcements.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No announcements yet</p>
            ) : (
              announcements.slice(0, 5).map(a => (
                <div key={a.id} onClick={() => handleClick(a)}
                  className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition ${!a.isRead ? 'bg-purple-50/50' : ''}`}>
                  <div className="flex items-start gap-2">
                    {!a.isRead && <span className="w-2 h-2 rounded-full bg-purple-600 mt-1.5 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!a.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{a.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{stripHtml(a.body)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(a.createdAt)}</p>
                    </div>
                    {a.priority === 'HIGH' && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">Urgent</span>}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100">
            <button onClick={handleViewAll} className="w-full text-center text-sm font-medium text-purple-600 hover:text-purple-800 transition">
              View All Announcements
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
